import React, { FormEvent, useMemo, useState } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import GridItem from '../../../layout/GridComponents/GridItem';
import makeStyles from '@mui/styles/makeStyles';
import { greys, mainColors } from '../../../../styling/theme';
import { formatESGWithUnit } from '../../../../utilities/numberFormatters';
import CustomTable from '../../../tables/CustomTable';
import { CustomColumn } from '../../../../types/components/tables/tableTypes';
import { normalizeFieldKey } from '../../../../utilities/normaliseFieldKey';
import LoadingSpinner from '../../../ui/LoadingSpinner';
import {
  getPreTradeCheckerResult,
  getPreTradeUploadResultErrorMessage,
  preTradeCheckerKeys,
  preTradeCheckerQueryOptions,
  PRE_TRADE_CHECKER_MAX_BATCH_ROWS,
  usePreTradeCheckerUploadMutation,
  type PreTradeCheckerBatchUploadResponse,
} from '../../../../api/rs-hg/esg/pre-trade-checker';
import { ExpectedFileShapePreview, FileUpload } from '../../../general/FileUploader';

type PendingBatchUpload = {
  uploadId: string;
  fileName: string;
};

type BatchUploadResult = {
  uploadId: string;
  fileName: string;
  response: PreTradeCheckerBatchUploadResponse;
};

const batchPendingRowId = (uploadId: string) => `batch-pending-${uploadId}`;

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.31rem',
    width: '100%',
    padding: '0.62rem 0.94rem',
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 500,
    color: mainColors.mainBlue,
  },
  descriptionGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.15rem',
    margin: '0.31rem 0 0 0',
  },
  description: {
    fontSize: '0.75rem',
    fontWeight: 400,
    color: mainColors.mainBlue,
  },
  inputFormContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    width: '100%',
    gap: '0.62rem',
  },
  input: {
    width: '18.75rem',
    height: '1.88rem',
    padding: '0.31rem 0.62rem',
    border: `1px solid ${mainColors.mainBlue}`,
    fontSize: '0.88rem',
    fontWeight: 400,
    margin: '0.62rem 0',
  },
  button_valid: {
    all: 'unset',
    width: 'fit-content',
    height: '1.88rem',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0 0.62rem',
    borderRadius: '0.25rem',
    fontWeight: 400,
    fontSize: '0.94rem',
    backgroundColor: mainColors.mainBlue,
    color: 'white',
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      backgroundColor: mainColors.mainBlue_lighter,
    },
    '&:active': {
      backgroundColor: mainColors.mainBlue_slightlyDarker,
    },
  },
  button_invalid: {
    all: 'unset',
    width: 'fit-content',
    height: '1.88rem',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0 0.62rem',
    borderRadius: '0.25rem',
    fontWeight: 400,
    fontSize: '0.94rem',
    backgroundColor: greys.grey200,
    color: 'white',
    userSelect: 'none',
  },
  noResults: {
    fontSize: '0.75rem',
    fontWeight: 500,
    margin: '0.31rem 0 0 0',
    color: mainColors.mainBlue,
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingBottom: '0.62rem',
  },
  responseMessage: {
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  tableRootOverflow: {
    overflow: 'visible !important',
    '& .MuiTableContainer-root': {
      overflow: 'visible !important',
    },
  },
  pendingCell: {
    position: 'absolute',
    left: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    zIndex: 2,
    whiteSpace: 'nowrap',
    paddingLeft: '0.9rem',
    paddingRight: '0.75rem',
    color: mainColors.mainBlue,
    fontStyle: 'italic',
  },
  pendingCellBackground: {
    backgroundColor: '#e8f0f8',
  },
  errorCellBackground: {
    backgroundColor: '#fdecea',
    color: mainColors.Fail,
    fontStyle: 'normal',
  },
  pendingSpinner: {
    height: '1rem',
    width: '1rem',
    borderWidth: '2px',
    flexShrink: 0,
  },
}));

interface PreTradeCheckerProps {
  fundId: string;
  positionDate: string | undefined;
}

type CheckStatus = 'pending' | 'success' | 'error';

interface CheckEntry {
  id: string;
  isin: string;
  status: CheckStatus;
  name?: string;
  errorMessage?: string;
  indicators?: {
    indicator: string;
    value: number | string;
    unit: string | null;
  }[];
}

const BATCH_PENDING_ROW_PREFIX = 'batch-pending-';

type PreTradeCheckerRow = {
  id: string;
  isin: string;
  name: string;
  status: CheckStatus;
  errorMessage?: string;
  [indicatorKey: string]: string | CheckStatus | undefined;
};

const PreTradeChecker: React.FC<PreTradeCheckerProps> = ({
  fundId,
  positionDate,
}) => {
  const classes = useStyles();
  const queryClient = useQueryClient();
  const preTradeUploadMutation = usePreTradeCheckerUploadMutation(
    fundId,
    positionDate ?? '',
    { showErrorToast: false },
  );

  const [isin, setIsin] = useState('');
  const [checkedIsins, setCheckedIsins] = useState<string[]>([]);
  const [pendingBatchUploads, setPendingBatchUploads] = useState<
    PendingBatchUpload[]
  >([]);
  const [batchResults, setBatchResults] = useState<BatchUploadResult[]>([]);

  const queryResults = useQueries({
    queries: checkedIsins.map((normalizedIsin) =>
      preTradeCheckerQueryOptions({
        fundId,
        positionDate: positionDate!,
        isin: normalizedIsin,
      }),
    ),
  });

  const individualChecks: CheckEntry[] = useMemo(() => {
    return checkedIsins.map((normalizedIsin, index) => {
      const query = queryResults[index];
      const baseEntry = {
        id: normalizedIsin,
        isin: normalizedIsin,
      };

      if (!query || query.isPending || query.isFetching) {
        return { ...baseEntry, status: 'pending' as const };
      }

      if (query.isError) {
        const error = query.error as AxiosError;
        const errorMessage =
          error.response?.status === 404
            ? 'ISIN not found.'
            : 'An unknown error occured. Please contact RiskSystem support.';

        return {
          ...baseEntry,
          status: 'error' as const,
          errorMessage,
        };
      }

      return {
        ...baseEntry,
        status: 'success' as const,
        name: query.data.name,
        indicators: query.data.indicators,
      };
    });
  }, [checkedIsins, queryResults]);

  const batchChecks: CheckEntry[] = useMemo(() => {
    const entries: CheckEntry[] = [];

    batchResults.forEach(({ uploadId, fileName, response: batchResult }) => {
      Object.entries(batchResult.succeeded ?? {}).forEach(([isin, result]) => {
        entries.push({
          id: `${uploadId}-${isin}`,
          isin,
          status: 'success',
          name: result.name,
          indicators: result.indicators,
        });
      });

      Object.entries(batchResult.check_errors ?? {}).forEach(([isin, error]) => {
        const reason = typeof error === 'string' ? error : error?.reason;
        entries.push({
          id: `${uploadId}-${isin}`,
          isin,
          status: 'error',
          errorMessage: reason || 'Check failed.',
        });
      });

      Object.entries(batchResult.parsing_errors ?? {}).forEach(([row, error]) => {
        entries.push({
          id: `${uploadId}-parse-${row}`,
          isin: error.value || `${fileName} — row ${row}`,
          status: 'error',
          errorMessage: error.reason || 'Could not parse row.',
        });
      });
    });

    return entries;
  }, [batchResults]);

  const checks: CheckEntry[] = useMemo(() => {
    const merged: CheckEntry[] = [...individualChecks];
    const seenIsins = new Set(individualChecks.map((check) => check.isin));

    for (const entry of batchChecks) {
      if (entry.id.includes('-parse-')) {
        merged.push(entry);
        continue;
      }
      if (!seenIsins.has(entry.isin)) {
        merged.push(entry);
        seenIsins.add(entry.isin);
      }
    }

    for (const pending of [...pendingBatchUploads].reverse()) {
      merged.unshift({
        id: batchPendingRowId(pending.uploadId),
        isin: pending.fileName,
        status: 'pending',
      });
    }

    return merged;
  }, [individualChecks, batchChecks, pendingBatchUploads]);

  const pendingIsinCount = individualChecks.filter(
    (check) => check.status === 'pending',
  ).length;
  const pendingBatchCount = pendingBatchUploads.length;

  const progressMessage = useMemo(() => {
    const parts: string[] = [];

    if (pendingIsinCount > 0) {
      parts.push(
        `${pendingIsinCount} ISIN check${pendingIsinCount === 1 ? '' : 's'} in progress`,
      );
    }

    if (pendingBatchCount > 0) {
      parts.push(
        `${pendingBatchCount} file upload${pendingBatchCount === 1 ? '' : 's'} processing`,
      );
    }

    if (!parts.length) {
      return null;
    }

    return `${parts.join(', ')} — results may take a while.`;
  }, [pendingIsinCount, pendingBatchCount]);

  const isValidIsin = (isin: string): boolean => {
    return isin.length === 12;
  };

  const { indicatorColumns, tableData } = useMemo(() => {
    if (!checks.length) {
      return {
        indicatorColumns: [] as { title: string; field: string }[],
        tableData: [] as PreTradeCheckerRow[],
      };
    }

    const indicatorTitlesOrdered: string[] = [];
    const seenTitles = new Set<string>();
    for (const check of checks) {
      for (const { indicator } of check.indicators || []) {
        if (!seenTitles.has(indicator)) {
          seenTitles.add(indicator);
          indicatorTitlesOrdered.push(indicator);
        }
      }
    }

    const usedKeys = new Set<string>();
    const indicatorKeyByTitle = new Map<string, string>();
    for (const title of indicatorTitlesOrdered) {
      const base = normalizeFieldKey(title);
      let key = base;
      let i = 2;
      while (usedKeys.has(key)) {
        key = `${base}_${i}`;
        i += 1;
      }
      usedKeys.add(key);
      indicatorKeyByTitle.set(title, key);
    }

    const columns = indicatorTitlesOrdered.map((title) => ({
      title,
      field: indicatorKeyByTitle.get(title) as string,
      cellStyle: {
        whiteSpace: 'nowrap',
        minWidth: '100px',
      },
    }));

    const data: PreTradeCheckerRow[] = checks.map((check) => {
      const row: PreTradeCheckerRow = {
        id: check.id,
        isin: check.isin,
        name: check.name || '',
        status: check.status,
        errorMessage: check.errorMessage,
      };

      for (const i of check.indicators || []) {
        const key = indicatorKeyByTitle.get(i.indicator);
        if (!key) continue;
        row[key] = formatESGWithUnit(i.value, i.unit ?? '');
      }
      return row;
    });

    return { indicatorColumns: columns, tableData: data };
  }, [checks]);

  const columns: CustomColumn<PreTradeCheckerRow>[] = useMemo(
    () => [
      { title: 'ISIN', field: 'isin' },
      {
        title: 'Name',
        field: 'name',
        cellStyle: (_name, rowData: PreTradeCheckerRow) => ({
          minWidth: '14rem',
          ...(rowData.status === 'pending' || rowData.status === 'error'
            ? {
              position: 'relative',
              overflow: 'visible',
            }
            : {}),
        }),
        render: (rowData: PreTradeCheckerRow) => {
          if (rowData.status === 'pending') {
            return (
              <div
                className={`${classes.pendingCell} ${classes.pendingCellBackground}`}
              >
                <LoadingSpinner className={classes.pendingSpinner} />
                {rowData.id.startsWith(BATCH_PENDING_ROW_PREFIX)
                  ? `Processing ${rowData.isin} — this may take a while`
                  : 'Checking — this may take a while'}
              </div>
            );
          }

          if (rowData.status === 'error') {
            return (
              <div
                className={`${classes.pendingCell} ${classes.errorCellBackground}`}
              >
                {rowData.errorMessage || 'Check failed.'}
              </div>
            );
          }

          return rowData.name;
        },
      },
      ...indicatorColumns,
    ],
    [
      classes.pendingCell,
      classes.pendingCellBackground,
      classes.errorCellBackground,
      classes.pendingSpinner,
      indicatorColumns,
    ],
  );

  const handleCheck = (event: FormEvent<HTMLFormElement>, ISIN: string) => {
    event.preventDefault();
    if (!fundId || !positionDate) {
      return;
    }

    const normalizedIsin = ISIN.toUpperCase();
    const queryKey = preTradeCheckerKeys.check(
      fundId,
      positionDate,
      normalizedIsin,
    );
    const isRecheck = checkedIsins.includes(normalizedIsin);

    setCheckedIsins((prevIsins) => {
      if (prevIsins.includes(normalizedIsin)) {
        return prevIsins;
      }

      return [normalizedIsin, ...prevIsins];
    });
    setIsin('');

    if (isRecheck) {
      queryClient.resetQueries({ queryKey });
      void queryClient.fetchQuery({
        queryKey,
        queryFn: () =>
          getPreTradeCheckerResult({
            fundId,
            positionDate,
            isin: normalizedIsin,
          }),
      });
    }
  };

  const handleBatchUploadStarted = ({
    uploadId,
    fileName,
  }: {
    uploadId: string;
    fileName: string;
  }) => {
    setPendingBatchUploads((prev) => [...prev, { uploadId, fileName }]);
  };

  const handleBatchUploadSuccess = ({
    uploadId,
    fileName,
    data,
  }: {
    uploadId: string;
    fileName: string;
    data: PreTradeCheckerBatchUploadResponse;
  }) => {
    setPendingBatchUploads((prev) =>
      prev.filter((pending) => pending.uploadId !== uploadId),
    );
    setBatchResults((prevResults) => [
      ...prevResults,
      { uploadId, fileName, response: data },
    ]);
  };

  const handleBatchUploadError = ({
    uploadId,
  }: {
    uploadId: string;
    fileName: string;
    message: string;
  }) => {
    setPendingBatchUploads((prev) =>
      prev.filter((pending) => pending.uploadId !== uploadId),
    );
  };

  return (
    <GridItem xs={12} card className={classes.root}>
      <div className={classes.title}>Pre-Trade Checker</div>
      <div className={classes.descriptionGroup}>
        <div className={classes.description}>
          Enter an ISIN and click Check, or use Batch Upload with a CSV or Excel file.
        </div>
        <div className={classes.description}>
          Results appear as they finish; you can queue more while others run.
        </div>
      </div>
      <form
        className={classes.inputFormContainer}
        onSubmit={(event: FormEvent<HTMLFormElement>) =>
          handleCheck(event, isin)
        }
      >
        <input
          type="text"
          placeholder="ISIN"
          value={isin}
          onChange={(e) => setIsin(e.target.value)}
          className={classes.input}
        />
        {isValidIsin(isin) ? (
          <button type="submit" className={classes.button_valid}>
            Check
          </button>
        ) : (
          <div className={classes.button_invalid}>Check</div>
        )}
        <FileUpload<PreTradeCheckerBatchUploadResponse>
          className="h-[1.88rem] w-fit cursor-pointer select-none justify-center rounded bg-mainblue px-[0.62rem] text-[0.94rem] font-normal text-white hover:bg-mainblue_hover active:bg-mainblue_hover"
          title="Batch Upload"
          onUpload={async ({ file, onProgress }) => {
            const response = await preTradeUploadMutation.mutateAsync({
              file,
              onProgress,
            });
            return response.data;
          }}
          validateResult={getPreTradeUploadResultErrorMessage}
          getUploadStatus={(data) => (data.full_success ? 'success' : 'partial')}
          expectedFilePreview={
            <ExpectedFileShapePreview
              shape={{
                columns: ['ISIN', 'Long Name'],
                exampleRows: [
                  ['JE00BF5FX167', '3I INFRASTRUCTURE PLC'],
                  ['IT0001207098', 'ACEA SPA'],
                  ['US00508Y1029', 'ACUITY INC'],
                  ['NL0000687663', 'AERCAP HOLDINGS NV'],
                ],
                description:
                  'Row 1 must contain the column headers ISIN and Long Name. Add one security per row below.',
                note: (
                  <p className="text-xs mt-2">
                    Due to batch limits only files with up to{' '}
                    <span className="font-bold text-black">
                      {PRE_TRADE_CHECKER_MAX_BATCH_ROWS}
                    </span>{' '}
                    rows are permitted per upload.
                  </p>
                ),
              }}
            />
          }
          onUploadStarted={handleBatchUploadStarted}
          onUploadSuccess={handleBatchUploadSuccess}
          onUploadError={handleBatchUploadError}
        />
        {progressMessage ? (
          <div
            className={classes.responseMessage}
            style={{ color: mainColors.mainBlue }}
          >
            {progressMessage}
          </div>
        ) : null}
      </form>
      {checks.length > 0 ? (
        <CustomTable<PreTradeCheckerRow>
          showToolbar
          title="Pre-Trade Results"
          exportFileName={`pre_trade_checker_${fundId}_${positionDate || 'na'}`}
          columns={columns}
          data={tableData}
          tableRootStyles={classes.tableRootOverflow}
          options={{
            exportButton: true,
            raptorExport: false,
            search: false,
            paging: false,
            sorting: false,
            rowStyle: (rowData: PreTradeCheckerRow) => {
              if (rowData.status === 'pending') {
                return {
                  backgroundColor: '#e8f0f8',
                  position: 'relative',
                };
              }
              if (rowData.status === 'error') {
                return {
                  backgroundColor: '#fdecea',
                  color: mainColors.Fail,
                  position: 'relative',
                };
              }
              return {};
            },
          }}
          noExport
          emptyDataSourceMessage="No results to show."
        />
      ) : (
        <div className={classes.noResults}>No results to show.</div>
      )}
    </GridItem>
  );
};

export default PreTradeChecker;
